<template>
  <div class="metering-point-info">
    <div class="section-content">
      <div class="section-header section-header-alt">
        <div class="section-header-left">
          <div class="section-title">METERING POINT :</div>
          <div v-if="isAddonType && hasLinkedMeterWithMpid" class="addon-header-note">
            Attached to meter: <span class="linked-meter-id-header">{{ linkedMeterLDN }}</span>
          </div>
        </div>
        <div class="action-buttons" v-if="!isAddonType">
          <button class="action-button" @click="openRemoveMpidOverlay" :disabled="!displayMeteringPoint">
            Remove from MPID
          </button>
        </div>
      </div>
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading metering point data...</span>
      </div>

      <div v-else-if="error && mpidValue && !isAddonType" class="error-state">
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="$emit('retry')">Retry</button>
      </div>

      <div v-else-if="displayMeteringPoint" class="device-details-grid">
        <div class="row">
          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">MPID :</div>
              <div class="device-info-value">
                <router-link
                    v-if="displayMeteringPoint?.mpid"
                    :to="`/mpids/${displayMeteringPoint.mpid}`"
                    class="mpid-link"
                    title="Open MPID details"
                >
                  {{ displayMeteringPoint.mpid }}
                </router-link>
                <span v-else>N/A</span>
              </div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">CITY :</div>
              <div class="device-info-value">{{ displayMeteringPoint.city || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">STREET :</div>
              <div class="device-info-value">{{ displayMeteringPoint.street || 'N/A' }}</div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.country">
              <div class="device-info-label">COUNTRY :</div>
              <div class="device-info-value">{{ displayMeteringPoint.country }}</div>
            </div>
          </div>

          <div class="col-6">
            <div class="device-info-row">
              <div class="device-info-label">CUSTOMER :</div>
              <div class="device-info-value">{{ displayMeteringPoint.customer_fname || 'N/A' }}</div>
            </div>
            <div class="device-info-row">
              <div class="device-info-label">ZIP CODE :</div>
              <div class="device-info-value">
                {{ displayMeteringPoint.zip_code || displayMeteringPoint.zipcode || 'N/A' }}
              </div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.lat && displayMeteringPoint.lon">
              <div class="device-info-label">COORDINATES :</div>
              <div class="device-info-value">
                {{ formatCoordinates(displayMeteringPoint.lat, displayMeteringPoint.lon) }}
              </div>
            </div>
            <div class="device-info-row" v-if="displayMeteringPoint.created_at">
              <div class="device-info-label">CREATED :</div>
              <div class="device-info-value">{{ formatDate(displayMeteringPoint.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!isAddonType" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>This device is not attached to any metering point.</p>
        <button class="action-button action-button-primary mt-3"
                @click="openAddMpidOverlay">
          Attach to MPID
        </button>
      </div>

      <div v-else-if="isAddonType && !hasActiveMeter" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>This ADDON device is not installed on any meter.</p>
        <p class="secondary-text">Radio devices cannot directly have a metering point. Please install this radio on a meter first.</p>
      </div>

      <div v-else-if="isAddonType && hasActiveMeter && !hasLinkedMeterWithMpid" class="no-metering-point">
        <div class="info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>The meter <span class="linked-meter-id">{{ linkedMeterLDN }}</span> that this radio is installed on has no metering point.</p>
        <p class="secondary-text">Please navigate to the meter device to attach it to a metering point.</p>
      </div>
    </div>

    <DevicesDetailsAddMpidOverlay
        v-if="showAddMpidOverlay"
        :isVisible="showAddMpidOverlay"
        :deviceId="deviceId"
        @close="closeAddMpidOverlay"
        @mpid-attached="handleMpidAttached"
    />
    <DeviceDetailsRemoveMpidOverlay
        v-if="showRemoveMpidOverlay"
        :isVisible="showRemoveMpidOverlay"
        :deviceId="deviceId"
        :currentMpid="displayMeteringPoint ? displayMeteringPoint.mpid : ''"
        @close="closeRemoveMpidOverlay"
        @mpid-removed="handleMpidRemoved"
    />
  </div>
</template>

<script>
// Script section remains exactly the same as the previous version
import DevicesDetailsAddMpidOverlay from './DevicesDetailsAddMpidOverlay.vue';
import DeviceDetailsRemoveMpidOverlay from './DeviceDetailsRemoveMpidOverlay.vue';

export default {
  name: 'DeviceDetailsMpid',
  components: {
    DevicesDetailsAddMpidOverlay,
    DeviceDetailsRemoveMpidOverlay
  },
  props: {
    meteringPoint: { type: Object, default: null },
    mpidValue: { type: String, default: '' },
    isLoading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    deviceId: { type: String, required: true },
    deviceType: { type: String, default: '' },
    linkedMeteringPoint: { type: Object, default: null },
    linkedMeterLDN: { type: String, default: '' },
    hasActiveMeter: { type: Boolean, default: false }
  },
  data() {
    return {
      showAddMpidOverlay: false,
      showRemoveMpidOverlay: false
    };
  },
  computed: {
    isAddonType() {
      return this.deviceType && this.deviceType.toUpperCase() === 'ADDON';
    },
    hasLinkedMeterWithMpid() {
      return !!this.linkedMeteringPoint && !!this.linkedMeterLDN;
    },
    displayMeteringPoint() {
      if (this.isAddonType && this.hasLinkedMeterWithMpid) {
        return this.linkedMeteringPoint;
      } else if (!this.isAddonType && this.meteringPoint) {
        return this.meteringPoint;
      }
      return null;
    },
    showActionButtons() {
      return !this.isAddonType && !!this.displayMeteringPoint;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
      }
    },
    formatCoordinates(lat, lon) {
      const numLat = parseFloat(lat);
      const numLon = parseFloat(lon);
      if (isNaN(numLat) || isNaN(numLon)) return 'N/A';
      return `${numLat.toFixed(4)}, ${numLon.toFixed(4)}`;
    },
    openAddMpidOverlay() {
      if (!this.isAddonType) {
        this.showAddMpidOverlay = true;
      } else {
        console.warn("Cannot directly attach an ADDON device to an MPID.");
      }
    },
    closeAddMpidOverlay() {
      this.showAddMpidOverlay = false;
    },
    handleMpidAttached(newMpid) {
      this.$emit('mpid-attached', newMpid);
      this.$emit('reload');
      this.closeAddMpidOverlay();
    },
    openRemoveMpidOverlay() {
      if (!this.isAddonType && this.displayMeteringPoint) {
        this.showRemoveMpidOverlay = true;
      }
    },
    closeRemoveMpidOverlay() {
      this.showRemoveMpidOverlay = false;
    },
    handleMpidRemoved(updatedDevice) {
      this.$emit('mpid-removed', updatedDevice);
      this.$emit('reload');
      this.closeRemoveMpidOverlay();
    }
  }
};
</script>

<style scoped>
/* Metering point info container */
.metering-point-info {
  height: 100%;
}

.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between; /* Keeps title/note left, buttons right */
  align-items: flex-start; /* Align items to the top */
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

/* *** NEW *** Container for title and optional addon note */
.section-header-left {
  display: flex;
  flex-direction: column; /* Stack title and note vertically */
  align-items: flex-start; /* Align text to the left */
  gap: 4px; /* Optional spacing between title and note */
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

/* *** NEW *** Styling for the addon note in the header */
.addon-header-note {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7); /* Slightly muted color */
  font-style: italic; /* Optional: make it italic */
}

/* *** NEW *** Styling for the meter ID within the header note */
.linked-meter-id-header {
  font-weight: 600; /* Make it stand out slightly */
  color: rgba(79, 195, 247, 0.9); /* Use existing linked ID color */
  font-style: normal; /* Override italics if parent is italic */
}

/* Device details grid styling (unchanged) */
.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

/* Loading state styles (unchanged) */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state styles (unchanged) */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255,255,255,0.1);
}

/* No metering point state (unchanged) */
.no-metering-point {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255,255,255,0.6);
  margin-bottom: 16px;
}

.no-metering-point p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

/* Action button styles (unchanged) */
.action-buttons {
  display: flex;
  gap: 10px;
  /* Ensure buttons stay aligned to the top if header-left grows */
  align-self: flex-start;
  /* Add slight top margin to align better if needed */
  /* margin-top: 2px; */
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-button-primary {
  background-color: rgba(63,81,181,0.2);
  border: 1px solid rgba(63,81,181,0.5);
}

.action-button-primary:hover {
  background-color: rgba(63,81,181,0.3);
}

.mt-3 {
  margin-top: 16px;
}

/* Responsive adjustments (unchanged) */
@media (max-width: 767.98px) {
  .section-header-alt {
    margin-top: 60px;
  }

  .device-info-row {
    margin-bottom: 15px;
  }
}

/* Original ADDON info note styling (unchanged) */
.addon-info-note {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 12px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-icon-small {
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.linked-meter-id {
  font-weight: 600;
  color: rgba(79, 195, 247, 0.9);
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
  margin-top: 6px;
  max-width: 80%;
}
.mpid-link {
  color: rgba(79, 195, 247, 0.9);
  text-decoration: none;
}
</style>